import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import { StaticTableFilterHook } from "commons/hooks/useStaticTableFilter";
import useStaticTableFilterWithMemory from "commons/hooks/useStaticTableFilterWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation, FilterType } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";

import { makeUrl } from "commons/utils/http";

import useSpecialOfferApi from "hooks/api/private/useSpecialOfferApi";
import useSnackbar from "hooks/useSnackbar";

import { SpecialOfferListItem } from "types/api/specialOffer";

export type SpecialOffersListHook = {
    navigateToNewSpecialOffer: () => void;
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    dataTableHook: DataTableHook<SpecialOfferListItem>;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    staticFilterHook: StaticTableFilterHook;
    alertConfirmHook: AlertConfirmHookType;
};

function useSpecialOffersList(): SpecialOffersListHook {
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    title: {
                        key: "title",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    subtitle: {
                        key: "subtitle",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        [],
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "special-offers-list-search",
        memoizedFieldsForSearch,
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const value = "" + event.target.value;

                newForm.search.title = value;
                newForm.search.subtitle = value;

                return newForm;
            }),
        [setFiltersForm],
    );

    const memoizedFieldsForStaticFilters = React.useMemo(
        () => ({
            language: {
                key: "language",
                operation: FilterOperation.Eq,
                defaultValue: "it",
            },
            enabled: {
                key: "enabled",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
        }),
        [],
    );

    const staticFilterHook = useStaticTableFilterWithMemory(
        "special-offers-list-static",
        memoizedFieldsForStaticFilters,
    );

    const { getAllSpecialOffer, deleteSpecialOffer } = useSpecialOfferApi();

    const navigate = useNavigate();

    const navigateToEditSpecialOffer = React.useCallback(
        (row: SpecialOfferListItem) => {
            navigate(makeUrl(`${row.id}/modifica`));
        },
        [navigate],
    );

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina offerta speciale",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteSpecialOfferButton = React.useCallback(
        (row: SpecialOfferListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare l'offerta speciale?", () => {
                const [apiCall] = deleteSpecialOffer({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Offerta speciale eliminata con successo");
                    })
                    .catch(() =>
                        enqueueErrorSnackbar("Errore nell'eliminazione dell'offerta speciale"),
                    );
            });
        },
        [
            enqueueSuccessSnackbar,
            setTriggerReloadTable,
            enqueueErrorSnackbar,
            openConfirmAlert,
            deleteSpecialOffer,
        ],
    );

    const columns: Array<Column<SpecialOfferListItem>> = React.useMemo(
        () => [
            {
                id: "title",
                header: "Titolo",
                render: (row: SpecialOfferListItem) => row.title,
            },
            {
                id: "subtitle",
                header: "Sottotitolo",
                render: (row: SpecialOfferListItem) => row.subtitle ?? "-",
            },
            {
                id: "enabled",
                header: "Visibile",
                width: "130px",
                render: (row: SpecialOfferListItem) => (row.enabled ? "Visibile" : "Non visibile"),
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: SpecialOfferListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={navigateToEditSpecialOffer}
                        onDelete={deleteSpecialOfferButton}
                    />
                ),
            },
        ],
        [navigateToEditSpecialOffer, deleteSpecialOfferButton],
    );

    const { filters: searchFilters } = filterSearchHook;
    const { filters: staticFilters } = staticFilterHook;

    const filters: FilterType[] = React.useMemo(
        () => [...searchFilters, ...staticFilters],
        [searchFilters, staticFilters],
    );

    const dataTableHook = useDataTableWithMemory<SpecialOfferListItem>({
        apiCall: getAllSpecialOffer,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        size: 20,
        key: "special-offers-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();

            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewSpecialOffer = React.useCallback(() => {
        navigate("/offerte-speciali/nuova");
    }, [navigate]);

    return React.useMemo(
        (): SpecialOffersListHook => ({
            onSearchFilterChange,
            navigateToNewSpecialOffer,
            filterSearchHook,
            staticFilterHook,
            alertConfirmHook,
            dataTableHook,
        }),
        [
            onSearchFilterChange,
            navigateToNewSpecialOffer,
            filterSearchHook,
            alertConfirmHook,
            staticFilterHook,
            dataTableHook,
        ],
    );
}

export default useSpecialOffersList;
